import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, Check, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

interface Props {
  matchId: string;
  compId: string;
  className?: string;
}

// Admin/founder-only panel listing open score flags raised by participants.
// Each flag can be resolved with a code: EDITED (admin already fixed),
// CONFIRMED_OCR (the OCR was right after all), or DUPLICATE (already covered).
export function ScoreFlagInbox({ matchId, compId, className }: Props) {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'OPEN' | 'RESOLVED' | 'DISMISSED' | undefined>('OPEN');

  const { data, isLoading } = useQuery({
    queryKey: ['scoreFlags', matchId, compId, statusFilter],
    queryFn: () => api.listScoreFlags(matchId, compId, statusFilter),
    refetchInterval: 30_000,
  });

  const resolveMut = useMutation({
    mutationFn: ({ sk, resolution }: { sk: string; resolution: 'EDITED' | 'CONFIRMED_OCR' | 'DUPLICATE' }) =>
      api.resolveScoreFlag(matchId, sk, compId, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoreFlags', matchId, compId] });
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
  });

  const flags = data?.flags ?? [];

  return (
    <div className={cn('rounded border border-iron-800 bg-iron-950 p-3', className)}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
          <Flag size={14} className="text-amber-400" /> Score flags
          {!isLoading && (
            <span className="rounded bg-iron-800 px-1.5 py-0.5 font-mono text-[10px] text-iron-400">
              {flags.length}
            </span>
          )}
        </div>
        <select
          value={statusFilter ?? ''}
          onChange={e => setStatusFilter((e.target.value || undefined) as typeof statusFilter)}
          className="rounded border border-iron-700 bg-iron-900 px-2 py-0.5 text-[10px] text-iron-200 focus:border-forge-500 focus:outline-none"
        >
          <option value="OPEN">Open</option>
          <option value="RESOLVED">Resolved</option>
          <option value="DISMISSED">Dismissed</option>
          <option value="">All</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-xs text-iron-500">Loading flags…</div>
      ) : flags.length === 0 ? (
        <div className="rounded border border-dashed border-iron-800 px-2 py-3 text-center text-xs text-iron-500">
          No {statusFilter?.toLowerCase() ?? ''} flags.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {flags.map(f => (
            <li
              key={f.sk}
              className="rounded border border-iron-800 bg-iron-900 p-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-iron-100">
                    G{f.gameNum} · <span className="text-forge-300">{f.playerKey}</span>
                    {' · '}
                    <span className="text-amber-300">{f.field === '*' ? 'whole row' : f.field}</span>
                  </div>
                  <div className="text-[10px] text-iron-500">
                    by {f.flaggerRole} <span className="font-mono">{f.flaggerName ?? `…${f.flaggerId.slice(-6)}`}</span>
                    {' · '}
                    {new Date(f.createdAt).toLocaleString()}
                    {f.status !== 'OPEN' && (
                      <span className="ml-2 rounded bg-iron-800 px-1 py-0.5 text-[9px] text-iron-300">
                        {f.status} {f.resolution ? `· ${f.resolution}` : ''}
                      </span>
                    )}
                  </div>
                  {f.note && (
                    <div className="mt-1 rounded bg-iron-950 p-1.5 text-xs italic text-iron-300">
                      "{f.note}"
                    </div>
                  )}
                </div>
                {f.status === 'OPEN' && (
                  <ResolveMenu
                    onPick={(resolution) => resolveMut.mutate({ sk: f.sk, resolution })}
                    pending={resolveMut.isPending}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ResolveMenu({ onPick, pending }: { onPick: (r: 'EDITED' | 'CONFIRMED_OCR' | 'DUPLICATE') => void; pending: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={pending}
        className="flex items-center gap-1 rounded bg-emerald-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        <Check size={10} /> Resolve <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded border border-iron-700 bg-iron-950 py-1 shadow-2xl">
          <ResolveOption label="Edited (I fixed it)" onClick={() => { setOpen(false); onPick('EDITED'); }} />
          <ResolveOption label="Confirmed OCR" onClick={() => { setOpen(false); onPick('CONFIRMED_OCR'); }} />
          <ResolveOption label="Duplicate" onClick={() => { setOpen(false); onPick('DUPLICATE'); }} />
        </div>
      )}
    </div>
  );
}

function ResolveOption({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-2 py-1 text-left text-xs text-iron-200 hover:bg-iron-800"
    >
      {label}
    </button>
  );
}
